import { useRef, useState } from 'react';
import { cn } from '@/lib/utils';
import { useWrestlerStats } from '@/features/profile/hooks/useWrestlerStats';
import { Trophy, Shield, Zap, Brain, Crosshair } from 'lucide-react';

/* ─── Card Assets (Icons & Types) ────────────────────────────── */

const StatIcon = ({ type, className }: { type: string, className?: string }) => {
    switch (type) {
        case 'offense': return <Crosshair className={className} />;
        case 'defense': return <Shield className={className} />;
        case 'conditioning': return <Zap className={className} />;
        case 'matIQ': return <Brain className={className} />;
        default: return <Trophy className={className} />;
    }
};

/* ─── 3D Tilt Wrapper ────────────────────────────────────────── */

export function WrestlerCard({ className }: { className?: string }) {
    const stats = useWrestlerStats();
    const cardRef = useRef<HTMLDivElement>(null);
    const [rotate, setRotate] = useState({ x: 0, y: 0 });
    const [glare, setGlare] = useState({ x: 50, y: 50, opacity: 0 });

    const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
        if (!cardRef.current) return;
        const rect = cardRef.current.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;

        // Calculate rotation (max 15deg)
        const centerX = rect.width / 2;
        const centerY = rect.height / 2;

        const rotateX = ((y - centerY) / centerY) * -10; // Invert Y for tilt
        const rotateY = ((x - centerX) / centerX) * 10;

        setRotate({ x: rotateX, y: rotateY });
        setGlare({
            x: (x / rect.width) * 100,
            y: (y / rect.height) * 100,
            opacity: 1
        });
    };

    const handleMouseLeave = () => {
        setRotate({ x: 0, y: 0 });
        setGlare(prev => ({ ...prev, opacity: 0 }));
    };


    /* ─── Dynamic Styles ─────────────────────────────────────────── */

    // Archetype Colors
    const getArchetypeColor = (arch: string) => {
        if (arch.includes('Takedown')) return 'text-status-error border-status-error/40';
        if (arch.includes('Defense') || arch.includes('Iron')) return 'text-category-defense border-category-defense/40';
        if (arch.includes('Conditioning')) return 'text-category-conditioning border-category-conditioning/40';
        return 'text-gold-base border-gold-base/40';
    };

    const archColorClass = getArchetypeColor(stats.archetype);

    return (
        <div
            className={cn("perspective-1000 relative w-full h-[540px] flex justify-center py-4", className)}
        >
            <div
                ref={cardRef}
                onMouseMove={handleMouseMove}
                onMouseLeave={handleMouseLeave}
                style={{
                    transform: `rotateX(${rotate.x}deg) rotateY(${rotate.y}deg)`,
                    transition: 'transform 0.1s ease-out'
                }}
                className={cn(
                    "w-full max-w-[340px] h-full rounded-[24px] relative overflow-hidden",
                    "bg-bg-elevated border border-white/10 shadow-2xl",
                    "select-none cursor-default"
                )}
            >
                {/* 1. Background Layer */}
                <div className="absolute inset-0 bg-gradient-to-br from-bg-surface2 via-bg-900 to-black z-0" />

                {/* 2. Holo/Glitter Effect */}
                <div className="absolute inset-0 opacity-20 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] mix-blend-overlay z-0 pointer-events-none" />

                {/* 3. Athlete Image (Dynamic) */}
                <div className="absolute inset-0 z-10 flex items-end justify-center pointer-events-none">
                    {/* Shadow Gradient */}
                    <div className="w-[120%] h-[90%] bg-gradient-to-t from-black via-gray-900 to-transparent opacity-80 mix-blend-multiply absolute bottom-0" />

                    {stats.avatar && (stats.avatar.startsWith('http') || stats.avatar.startsWith('/') || stats.avatar.startsWith('data:')) ? (
                        <img
                            src={stats.avatar}
                            alt="Athlete"
                            className="h-[85%] object-cover grayscale contrast-125 drop-shadow-2xl translate-y-8 mask-image-gradient"
                        />
                    ) : stats.avatar ? (
                        <div className="absolute inset-0 flex items-center justify-center translate-y-12">
                            <span className="text-[140px] leading-none filter drop-shadow-2xl opacity-90 grayscale-[0.3]">{stats.avatar}</span>
                        </div>
                    ) : (
                        /* Fallback silhouette if no image */
                        <div className="absolute bottom-0 w-full h-3/4 bg-contain bg-center bg-no-repeat opacity-40 translate-y-12 blur-[1px]"
                            style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg width='200' height='400' viewBox='0 0 200 400' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M100 50C120 50 135 75 130 100C125 125 140 140 160 160C180 180 180 250 160 300L140 400H60L40 300C20 250 20 180 40 160C60 140 75 125 70 100C65 75 80 50 100 50Z' fill='%23333'/%3E%3C/svg%3E")` }}
                        />
                    )}
                </div>

                {/* 4. OVR Badge (Top Left) */}
                <div className="absolute top-6 left-6 z-20 flex flex-col items-center">
                    <div className="relative">
                        <span className="text-[56px] font-black italic leading-none tracking-tighter text-transparent bg-clip-text bg-gradient-to-br from-gold-100 to-gold-shadow drop-shadow-lg">
                            {stats.ovr}
                        </span>
                    </div>
                    <span className="text-[12px] font-bold uppercase tracking-widest text-gold-base/80 mt-[-4px]">
                        OVR
                    </span>

                    <div className="mt-2 w-12 h-1 bg-gradient-to-r from-transparent via-gold-base to-transparent opacity-50" />

                    <div className="mt-2 flex flex-col items-center">
                        <span className="text-[14px] font-bold text-white leading-none">POS</span>
                        <span className="text-[10px] text-white/50 font-medium">NEUTRAL</span>
                    </div>
                </div>

                {/* 5. Archetype Badge (Top Right) */}
                <div className="absolute top-6 right-6 z-20 text-right">
                    <div className={cn("px-3 py-1 rounded-full border bg-black/40 backdrop-blur-md", archColorClass)}>
                        <span className="text-[10px] font-bold uppercase tracking-wider">
                            {stats.archetype}
                        </span>
                    </div>
                    <div className="mt-3 flex items-center justify-end gap-1">
                        <span className="text-[10px] font-bold text-gold-base">LVL {stats.level}</span>
                        <div className="w-16 h-1 bg-white/10 rounded-full overflow-hidden">
                            <div
                                className="h-full bg-gold-base"
                                style={{ width: `${(stats.xp % 100)}%` }}
                            />
                        </div>
                    </div>
                </div>

                {/* 6. Rating Panels (Bottom) */}
                <div className="absolute bottom-0 inset-x-0 p-6 z-30 bg-gradient-to-t from-black via-black/90 to-transparent pt-24">
                    <div className="flex items-end justify-between mb-6">
                        <div>
                            <h2 className="text-[28px] font-black text-white leading-none uppercase italic tracking-tight">
                                Wrestler
                            </h2>
                            <span className="text-[14px] font-medium text-white/60 block mt-1">
                                152 lbs • Varsity
                            </span>
                        </div>
                    </div>

                    <div className="grid grid-cols-4 gap-2">
                        <StatBox label="OFF" value={stats.subRatings.offense} type="offense" />
                        <StatBox label="DEF" value={stats.subRatings.defense} type="defense" />
                        <StatBox label="IQ" value={stats.subRatings.matIQ} type="matIQ" />
                        <StatBox label="CND" value={stats.subRatings.conditioning} type="conditioning" />
                    </div>
                </div>

                {/* Glare Overlay */}
                <div
                    className="absolute inset-0 z-40 pointer-events-none mix-blend-overlay"
                    style={{
                        background: `radial-gradient(circle at ${glare.x}% ${glare.y}%, rgba(255,255,255,0.3) 0%, transparent 60%)`,
                        opacity: glare.opacity,
                        transition: 'opacity 0.2s ease-out'
                    }}
                />

                {/* Border Highlight */}
                <div className="absolute inset-0 border border-gold-base/20 rounded-[24px] z-50 pointer-events-none" />
            </div>
        </div>
    );
}

function StatBox({ label, value, type }: { label: string, value: number, type: string }) {
    // Color coding based on value
    const getColor = (v: number) => {
        if (v >= 90) return 'text-gold-400';
        if (v >= 75) return 'text-status-success'; // Green
        if (v >= 60) return 'text-white';
        return 'text-white/60';
    };

    return (
        <div className="flex flex-col items-center bg-white/[0.03] rounded-lg py-2 border border-white/[0.05] relative overflow-hidden">
            <div className="absolute top-0 right-0 p-1 opacity-20">
                <StatIcon type={type} className="w-4 h-4 text-white" />
            </div>
            <span className="text-[10px] font-bold uppercase tracking-wider text-white/40 mb-1 z-10">
                {label}
            </span>
            <span className={cn("text-[20px] font-bold leading-none z-10", getColor(value))}>
                {value}
            </span>
        </div>
    );
}
